'use client';
import Link from 'next/link';
import { useState } from 'react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { HoverIconTSX } from '@/components/ui/hover-icon-tsx';
import {
  CabinetIcon,
  CabinetWhiteIcon,
  CommunityIcon,
  LogoutIcon,
  LogoutWhiteIcon,
  ProfileIcon,
  ProfileWhiteIcon,
  RewardsIcon,
  StatisticsIcon,
} from '@/components/icons';
import { AlertModal } from '../shared/modals/alert-modal';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

interface UserMenuProps {
  userData: any;
  user: any;
  onLogout: () => void;
}

export function UserMenu({ userData, user, onLogout }: UserMenuProps) {
  const [isOpenAlertModal, setIsOpenAlertModal] = useState(false);
  const [isOpenPopover, setIsOpenPopover] = useState(false);
  const profileId = userData?.id ?? user?.id;
  const profileHref = profileId != null ? `/author/${profileId}` : '/profile';

  return (
    <Popover open={isOpenPopover} onOpenChange={setIsOpenPopover}>
      <PopoverTrigger asChild>
        <button
          onClick={() => setIsOpenPopover(!isOpenPopover)}
          className="w-10 h-10 rounded-full bg-[#3a3f47] hover:bg-[#4a4f57] flex items-center justify-center transition-colors overflow-hidden"
        >
          <Avatar className="w-10 h-10">
            <AvatarImage
              className="object-cover"
              src={userData?.avatar ? userData.avatar.thumbnailURL || userData.avatar.url : ''}
              width={40}
              height={40}
              quality={80}
            />
            <AvatarFallback className="text-sm">
              {userData?.name?.[0] || userData?.email?.[0] || 'U'}
            </AvatarFallback>
          </Avatar>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="max-w-52 p-2 bg-[#F2F3F7] rounded-2xl shadow-[0_20px_30px_rgba(0,0,0,0.2)]"
        align="end"
      >
        <div className="w-full">
          <div className="px-2">
            <div className="flex flex-col items-start space-y-3">
              <Avatar className="w-10 h-10">
                <AvatarImage
                  src={userData?.avatar ? userData.avatar.thumbnailURL || userData.avatar.url : ''}
                  className="object-cover"
                />
                <AvatarFallback className="text-lg">
                  {userData?.name?.[0] || userData?.email?.[0] || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="text-left pb-2">
                <p className="text-lg font-semibold text-black break-all">
                  {userData?.name || user?.name}
                </p>
                {/* <p className="text-sm text-[#BEBEBE]">
                  @
                  {userData?.username ||
                    userData?.email?.split('@')[0] ||
                    'username'}
                </p> */}
              </div>
            </div>
          </div>
          <hr className=" border-white m-auto mb-1" />
          <div className="">
            <Link
              href="/author-dashboard"
              onClick={() => setIsOpenPopover(false)}
              className="w-full flex items-center px-2 py-2 text-sm text-black hover:bg-[#505CFF] hover:text-white rounded-lg transition-colors mb-2 group"
            >
              <HoverIconTSX
                Icon={CabinetIcon}
                HoverIcon={CabinetWhiteIcon}
                alt="Кабинет автора"
                width={16}
                height={16}
                className="mr-2 group-hover:brightness-0 group-hover:invert"
              />
              Кабинет автора
            </Link>
            <hr className="w-[94%] border-white m-auto -mt-1" />
            <Link
              href={profileHref}
              onClick={() => setIsOpenPopover(false)}
              className="w-full flex items-center justify-between px-2 py-2 text-sm text-black hover:bg-[#505CFF] hover:text-white rounded-lg transition-colors group"
            >
              <div className="flex items-center">
                <HoverIconTSX
                  Icon={ProfileIcon}
                  HoverIcon={ProfileWhiteIcon}
                  alt="Мой профиль"
                  width={16}
                  height={16}
                  className="mr-2 group-hover:brightness-0 group-hover:invert"
                />
                Мой профиль
              </div>
            </Link>

            <div className="w-full flex items-center gap-2 px-2 py-2 text-sm text-[#BEBEBE] rounded-lg transition-colors">
              <div className="flex items-center">
                <CommunityIcon width={16} height={16} className="mr-2" />
                Коммьюнити
              </div>
              <span className="px-1 py-0.5 text-xs bg-white text-[#575E74] rounded-lg">Скоро!</span>
            </div>

            <div className="w-full flex items-center gap-2 px-2 py-2 text-sm text-[#BEBEBE] rounded-lg transition-colors">
              <div className="flex items-center">
                <RewardsIcon width={16} height={16} className="mr-2" />
                Награды
              </div>
              <span className="px-1 py-0.5 text-xs bg-white text-[#575E74] rounded-lg">Скоро!</span>
            </div>

            <div className="w-full flex items-center gap-2 px-2 py-2 text-sm text-[#BEBEBE] rounded-lg transition-colors">
              <div className="flex items-center">
                <StatisticsIcon width={16} height={16} className="mr-2" />
                Статистика
              </div>
              <span className="px-1 py-0.5 text-xs bg-white text-[#575E74] rounded-lg">Скоро!</span>
            </div>

            <div className="border-t border-white my-2"></div>
            <AlertModal
              open={isOpenAlertModal}
              onOpenChange={setIsOpenAlertModal}
              onConfirm={() => {
                onLogout();
              }}
              trigger={
                <button className="w-full flex items-center px-2 py-2 text-sm text-black hover:bg-[#505CFF] hover:text-white rounded-lg transition-colors group">
                  <HoverIconTSX
                    Icon={LogoutIcon}
                    HoverIcon={LogoutWhiteIcon}
                    alt="Выйти из профиля"
                    width={16}
                    height={16}
                    className="mr-2 group-hover:brightness-0 group-hover:invert"
                  />
                  Выйти из профиля
                </button>
              }
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
